import React from "react";
import { i18n } from "@/lib/api";
import { GlassesIcon } from "./GlassesIcon";

export default function WelcomeScreen({ language, onLanguageChange, onChoose }) {
  const t = i18n[language];
  const isEs = language === "es";

  const featuresEn = [
    "500 AI generation credits every month, plus $10 top-up packs that never expire",
    "Ten specialized modules \u2014 lesson plans, slide decks, exams, study guides, essay grading, recommendation letters, and more",
    "Eight-agent verification pipeline with mandatory Google Search Grounding on every output",
    "Bilingual workspace (English / Espanol) with voice auto-detect on the microphone",
    "Clickable academic citations with DOI links \u2014 institutional library proxy supported",
    "Private document vault \u2014 keep everything forever, even after you cancel",
    "Cancel anytime through the Stripe customer portal",
  ];

  const featuresEs = [
    "500 creditos de generacion IA cada mes, mas paquetes de recarga de $10 que nunca expiran",
    "Diez modulos especializados \u2014 planes de leccion, presentaciones, examenes, guias de estudio, evaluacion de ensayos, cartas de recomendacion y mas",
    "Canalizacion de verificacion de ocho agentes con Google Search Grounding obligatorio en cada salida",
    "Espacio bilingue (Ingles / Espanol) con deteccion automatica de voz en el microfono",
    "Citas academicas clickeables con enlaces DOI \u2014 proxy institucional de biblioteca soportado",
    "Biblioteca privada de documentos \u2014 todo se conserva para siempre, incluso despues de cancelar",
    "Cancela en cualquier momento desde el portal de cliente Stripe",
  ];

  const features = isEs ? featuresEs : featuresEn;

  return (
    <div data-testid="welcome-screen" style={{ maxWidth: 720, margin: "0 auto", padding: "3rem 1.5rem 4rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2.5rem" }}>
        <GlassesIcon size={52} />
        <div className="lang-toggle" data-testid="welcome-lang-toggle">
          <button
            data-testid="welcome-lang-en"
            className={language === "en" ? "active" : ""}
            onClick={() => onLanguageChange("en")}
          >
            EN
          </button>
          <button
            data-testid="welcome-lang-es"
            className={language === "es" ? "active" : ""}
            onClick={() => onLanguageChange("es")}
          >
            ES
          </button>
        </div>
      </div>

      <div className="rise rise-1">
        <div className="h-eyebrow">GO OUTDOORS</div>
        <h1 className="h-display" style={{ fontSize: "2.7rem", marginTop: "0.5rem", lineHeight: 1.05 }} data-testid="welcome-headline">
          POCKET TA AI
        </h1>
        <div className="h-eyebrow" style={{ marginTop: "0.6rem" }} data-testid="welcome-tagline">
          {t.tagline}
        </div>
      </div>

      <div className="line-strong rise rise-2" />

      <p className="rise rise-2 txt-mute" style={{ fontSize: "1.05rem", lineHeight: 1.6, marginBottom: "2.5rem" }} data-testid="welcome-subtitle">
        {isEs
          ? "Un asistente docente automatizado, tutor de investigacion bajo demanda y analizador de medios visuales para profesores universitarios. Construido para profundidad academica, no para ruido."
          : "An automated teaching assistant, on-demand research tutor, and visual media analyzer for university professors. Built for academic depth, not hype."}
      </p>

      <div className="rise rise-3" style={{ marginBottom: "2.5rem" }}>
        <div className="h-eyebrow">{isEs ? "Suscripcion mensual" : "Monthly Subscription"}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.85rem", marginTop: "0.45rem" }}>
          <span
            className="h-display"
            style={{ fontSize: "3.6rem", letterSpacing: "-0.04em", color: "var(--accent)" }}
            data-testid="welcome-price"
          >
            $19.99
          </span>
          <span className="txt-mute" style={{ fontSize: "1.05rem" }}>
            {isEs ? "por mes" : "per month"}
          </span>
        </div>
      </div>

      <div className="rise rise-4" style={{ marginBottom: "2.5rem" }}>
        <div className="h-eyebrow">{isEs ? "Que recibes" : "What you get"}</div>
        <ol style={{ listStyle: "none", padding: 0, marginTop: "0.85rem" }} data-testid="welcome-features-list">
          {features.map((f, i) => (
            <li
              key={i}
              style={{
                padding: "0.85rem 0",
                borderBottom: "1px solid var(--line)",
                display: "grid",
                gridTemplateColumns: "2.4rem 1fr",
                gap: "0.85rem",
                alignItems: "baseline",
              }}
              data-testid={`welcome-feature-${i + 1}`}
            >
              <span className="mono txt-faint" style={{ fontSize: "0.78rem", letterSpacing: "0.05em" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span style={{ fontSize: "0.96rem", lineHeight: 1.55 }}>{f}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="rise rise-5" style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1.5rem" }}>
        <button
          data-testid="welcome-create-account"
          className="btn-primary"
          style={{ width: "100%", padding: "0.95rem 1.25rem", fontSize: "1.02rem" }}
          onClick={() => onChoose("register")}
        >
          {isEs ? "Crear Cuenta" : "Create Account"}
        </button>
        <button
          data-testid="welcome-sign-in"
          className="btn-link"
          style={{ textAlign: "center", padding: "0.5rem", borderBottom: "none", color: "var(--ink-mute)" }}
          onClick={() => onChoose("login")}
        >
          {isEs
            ? "Ya tienes una cuenta? Inicia sesion"
            : "Already have an account? Sign in"}
        </button>
      </div>

      <div className="line" />
      <p className="txt-faint" style={{ fontSize: "0.85rem", textAlign: "center", marginTop: "1rem" }} data-testid="welcome-security-note">
        {isEs
          ? "Almacenamiento cifrado. Sin reventa de datos. Propiedad y operada por GO OUTDOORS."
          : "Encrypted storage. No data resale. Owned and operated by GO OUTDOORS."}
      </p>
    </div>
  );
}
